import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as paymentInputActionCreators from '@makemydeal/dr-dash-store/actions/paymentInput';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as dealerDiscountsSelectors from '@makemydeal/dr-dash-store/selectors/dealerDiscountsSelectors';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, SELLING_PRICE } from '../../../constants';
import { dealLockHooks } from '../../../hooks';
import { usePaymentCall } from '../../../utils/usePaymentCall';
import { MAX_SELLING_PRICE_THRESHOLD, NUMERIC_MAX_VALUE_MESSAGE } from '../constants';
import { useFieldValidationEffect } from './useFieldValidationEffect';

export const useSellingPriceField = () => {
    const dispatch = useDispatch();

    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const discountTotalLabel: number = useSelector(dealerDiscountsSelectors.getSummaryDiscountTotalLabel) || 0;
    const sellingPriceValidationError = useSelector(
        dealScreenErrorSelectors.getFieldValidationError(DealTabs.SUMMARY, kebabCase(SELLING_PRICE))
    );
    const isItemizedDiscountsEnabledForDealer = useSelector(dealerSelectors.isItemizedDiscountsEnabledForDealer);

    const {
        value: sellingPrice,
        handleChange: handleSellingPriceChange,
        handleBlur,
        handleKeyDown
    } = usePaymentCall(offerSelectors.getSellingPrice, paymentInputActionCreators.updateOfferPrice, {
        formatValueToString: formatNumberAndCents
    });
    const isLocked = dealLockHooks.useIsDealLocked();
    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        handleSellingPriceChange(e);
    };

    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_OFFER_PRICE_IMMEDIATE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    const handleOfferType = () => {
        if (offerType === CASH) dispatch(interactionAnalyticsActionCreators.cashSellingPriceTextInput());
        else if (offerType === FINANCE) dispatch(interactionAnalyticsActionCreators.financeSellingPriceTextInput());
        else if (offerType === LEASE) dispatch(interactionAnalyticsActionCreators.leaseSellingPriceTextInput());
    };

    useFieldValidationEffect({
        isEnabled: isDealScreenRedDotErrorsEnabled,
        dispatch,
        failed,
        value: sellingPrice,
        threshold: MAX_SELLING_PRICE_THRESHOLD,
        validationError: sellingPriceValidationError,
        fieldName: SELLING_PRICE
    });

    return {
        label: SELLING_PRICE,
        discountTotal: discountTotalLabel,
        hasDiscountApplied: !!discountTotalLabel,
        isItemizedDiscountsEnabledForDealer,
        isLocked,
        inputProps: {
            name: kebabCase(SELLING_PRICE),
            onChange: handleChange,
            placeholder: '26,500.00',
            hasError: failed,
            errorMessage: failedMessage,
            value: sellingPrice,
            onBlur: (e: any) => {
                handleBlur(e);
                handleOfferType();
            },
            'data-testid': kebabCase(SELLING_PRICE),
            disabled: isLocked,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            inputPrefix: '$',
            autoInsertCommas: true,
            maxValue: MAX_SELLING_PRICE_THRESHOLD,
            customErrorMessages: {
                exceedsMaxValueRule: NUMERIC_MAX_VALUE_MESSAGE
            },
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
